import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Paper,
} from "@mui/material";

import LogoImage from "../../assets/logo.png";
import IntroGif from "../../assets/intro.gif";

export default function OnboardingLanding({ open, onContinue }) {
  return (
    <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <Box sx={{ position: "relative", pt: 2, textAlign: "center" }}>
          <Box
            component="img"
            src={LogoImage}
            alt="Downstream logo"
            sx={{
              position: "absolute",
              top: -10,
              right: -10,
              height: 100,
              width: "auto",
            }}
          />

          <Typography variant="h4" component="div" gutterBottom>
            Welcome to Downstream
          </Typography>

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            A Learning Analytics Dashboard for Student Success
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph sx={{ whiteSpace: "pre-line" }}>
            {`Downstream helps you better understand and support your students by providing
insights into their motivation, engagement, and learning behaviors—directly within your Canvas course.

This tool is designed to make it easier to identify students who may need support and to give you
clear and actionable information to improve learning outcomes without adding to your workload.`}
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Quick Introduction
          </Typography>

          <Paper
            elevation={3}
            sx={{
              maxWidth: 700,
              mx: "auto",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={IntroGif}
              alt="Downstream Introduction"
              sx={{
                width: "100%",
                display: "block",
              }}
            />
          </Paper>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          What Downstream Provides
        </Typography>

        <List sx={{ py: 0 }}>
          <ListItem sx={{ py: 0.5 }}>
            <ListItemText
              sx={{ my: 0 }}
              primary="AI-Enhanced Early Alerts"
              secondary="Identify potential academic risk using course activity indicators and predictive analytics, while Chiron, Downstream’s AI agent, helps generate recommended early alert categories for instructor review."
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <ListItem sx={{ py: 0.5 }}>
            <ListItemText
              sx={{ my: 0 }}
              primary="Reflection Surveys"
              secondary="Easily deploy short, automated surveys through Canvas quizzes to understand student motivation, engagement, and learning experiences. No set–up required."
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <ListItem sx={{ py: 0.5 }}>
            <ListItemText
              sx={{ my: 0 }}
              primary="Real-Time Analytics"
              secondary="Monitor trends across your course, including changes in student engagement, participation, and academic risk over time."
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <ListItem sx={{ py: 0.5 }}>
            <ListItemText
              sx={{ my: 0 }}
              primary="Seamless Canvas Integration"
              secondary="No additional platforms, logins, or technical setup are required. Downstream works entirely within your Canvas course. "
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onContinue}
          sx={{
            minWidth: 200,
            background: "linear-gradient(135deg, #2FA4C7, #5DBB3A)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #1F8FA3, #4FA52F)",
            },
          }}
        >
          Get Started
        </Button>
      </DialogActions>
    </Dialog>
  );
}